import { ApiPrewarming, ApiProductSize, ApiProductStockDetail } from 'idt-ecommerce-api/_api/api-catalog/api-products/api-products.models';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { CurrencyInfo } from 'idt-ecommerce-api/stores/stores.models';
import { MoneyHelpers } from 'idt-ecommerce-api/util/money.helpers';
// eslint-disable-next-line no-restricted-imports
import { GrowthbookConfig } from 'render/services/render-growthbook-configs/render-growthbook-configs.service';

import { ProductPricePrewarming, ProductSize } from '../products.models';


export class SizesModelsTransformer {

  /**
   * Function that cleans the list of sizes and maintains those that have stock in the event that a repeated one does
   * not have it
   * @param apiProductSize list of sizes with repeat sizes
   * @param productStock lis of sizes with stock
   * @param useFetchStock flag to indicate if we want to use the stock of the sizes
   */
  public static filterRepeatSizes(
    apiProductSize: ApiProductSize[],
    productStock: ApiProductStockDetail[] = [],
    useFetchStock: boolean = true
  ): ApiProductSize[] {
    const orderedKeys: string[] = [];
    if (useFetchStock) {
      const filteredSizes = apiProductSize.reduce((
        result: { [key: string]: ApiProductSize },
        productSize: ApiProductSize
      ): { [key: string]: ApiProductSize } => {
        const hasProductSizeWithStock = productStock.some((sizeWidthStock) => {
          return sizeWidthStock.availability === 'in_stock' && sizeWidthStock.id === productSize.sku;
        });
        const key = productSize.sizeType ? `${productSize.name}_${productSize.sizeType}` : productSize.name;

        if (hasProductSizeWithStock || !result[key]) {
          result[key] = productSize;
          orderedKeys.push(key);
        }
        return result;
      }, {});

      return orderedKeys.map((key) => filteredSizes[key]);
    }

    // Define the priority order for visibilityValue
    const priorityOrder = {
      SHOW: 1,
      RUNNING_OUT: 2,
      BACK_SOON: 3,
      COMING_SOON: 4,
      SOLD_OUT: 5,
      HIDDEN: 6
    };

    const sizesByNameAndType: { [key: string]: ApiProductSize } = {};

    apiProductSize.forEach((item) => {
      const key = item.sizeType ? `${item.name}_${item.sizeType}` : item.name;

      if (!sizesByNameAndType[key]) {
        sizesByNameAndType[key] = item;
        orderedKeys.push(key);
      }

      const currentPriority = priorityOrder[item.visibilityValue || 'HIDDEN'];
      const existingPriority = priorityOrder[sizesByNameAndType[key].visibilityValue || 'HIDDEN'];

      if (currentPriority < existingPriority) {
        sizesByNameAndType[key] = item;
      }
    });

    return orderedKeys.map((key) => sizesByNameAndType[key]);
  }

  public static createProductSize(
    apiSize: ApiProductSize,
    currencyInfo: CurrencyInfo,
    internationalSizeName: string | undefined,
    productStock: ApiProductStockDetail[] = []
  ): ProductSize {
    const sizeStock = productStock.find((stock: ApiProductStockDetail) => stock.id === apiSize.sku);
    const price: number = Number.parseInt((apiSize?.price ?? ''), 10);
    const oldPrice: number = Number.parseInt((apiSize?.oldPrice ?? ''), 10);
    const oldPriceDiscount = apiSize?.discountsPercentages?.oldPriceDiscount;
    const oldFuturePriceDiscount = apiSize?.discountsPercentages?.oldFuturePriceDiscount;
    const originalPrice: number = Number.parseInt((apiSize?.originalPrice ?? ''), 10);
    const originalPriceDiscount = apiSize?.discountsPercentages?.originalPriceDiscount;
    const discountsPercentages = {
      ...(oldPriceDiscount && { oldPriceDiscount }),
      ...(oldFuturePriceDiscount && { oldFuturePriceDiscount }),
      ...(originalPriceDiscount && { originalPriceDiscount })
    };
    const promotion = { ...(apiSize.promotionId && { promotionId: apiSize.promotionId }) };

    const result: ProductSize = {
      sku: apiSize.sku.toString(),
      masterSizeId: apiSize.mastersSizeId,
      name: apiSize.name,
      partnumber: apiSize.partnumber,
      isBuyable: apiSize.isBuyable,
      backSoon: apiSize.backSoon,
      description: apiSize.description,
      priceInfo: {
        ...(!Number.isNaN(price) && { price }),
        ...(!Number.isNaN(oldPrice) && { oldPrice }),
        ...(!Number.isNaN(originalPrice) && { originalPrice }),
        priceWithCurrencySymbol: price ? MoneyHelpers.formatPriceWithCurrencySymbol(price, currencyInfo) : '',
        oldPriceWithCurrencySymbol: oldPrice ? MoneyHelpers.formatPriceWithCurrencySymbol(oldPrice, currencyInfo) : '',
        discount: MoneyHelpers.getDiscountFromPrice(price, oldPrice),
        prewarming: this.prewarmingFromApi(apiSize.futurePrice, currencyInfo),
        ...((oldPriceDiscount || oldFuturePriceDiscount || originalPriceDiscount) && { discountsPercentages }),
        ...(apiSize.promotionId !== undefined && !Number.isNaN(apiSize.promotionId) && { promotion })
      },
      availability: (sizeStock && (sizeStock.availability === 'in_stock' || sizeStock.availability === 'out_of_stock'))
        ? sizeStock.availability
        : 'unknown',
      visibilityValue: apiSize.visibilityValue,
      typeThreshold: sizeStock?.typeThreshold,
      sizeType: apiSize.sizeType
    };

    if (internationalSizeName) {
      result.internationalSizeName = internationalSizeName;
    }

    return result;
  }

  public static productSizeObjectFromApi(
    apiProductSize: ApiProductSize[],
    productStock: ApiProductStockDetail[],
    context: EcommerceContext,
    productType?: string,
    growthbookConfig: GrowthbookConfig[] = []
  ): ProductSize[] {
    const productSizes: ProductSize[] = [];
    const currencyInfo: CurrencyInfo | undefined = context.store.details.currencyInfo;

    if (currencyInfo) {
      const fetchStocksConfig = SizesModelsTransformer.getGrowthbookConfigurationsParams(growthbookConfig, 'product.fetch-stocks');
      const isFetchStock = fetchStocksConfig === undefined ? true : !!fetchStocksConfig;
      const apiProductSizeFiltered: ApiProductSize[] = SizesModelsTransformer.filterRepeatSizes(
        apiProductSize, productStock, isFetchStock
      );
      const clothingMapping = SizesModelsTransformer.getGrowthbookConfigurationsParams(growthbookConfig, 'size.clothing.mapping');
      const shoesMapping = SizesModelsTransformer.getGrowthbookConfigurationsParams(growthbookConfig, 'size.shoes.mapping');

      for (const apiSize of apiProductSizeFiltered) {
        let internationalSizeName;

        if (productType?.toLowerCase() === 'clothing' && typeof clothingMapping === 'object') {
          internationalSizeName = clothingMapping?.[apiSize.name];
        } else if (productType?.toLowerCase() === 'footwear' && typeof shoesMapping === 'object') {
          internationalSizeName = shoesMapping?.[apiSize.name];
        }

        const productSize: ProductSize = SizesModelsTransformer.createProductSize(
          apiSize, currencyInfo, internationalSizeName, productStock
        );
        productSizes.push(productSize);
      }
    }

    return productSizes;
  }

  /**
   * Parsea los datos de prewarming de la talla provinientes del API añadiendo el
   * campo priceWithCurrency
   * @param prewarming Datos de prewarming asociados a una talla
   * @param currencyInfo Información de la moneda asociada al eCommerceContext en el que nos encontramos
   * @returns Datos completos de prewarming
   */
  public static prewarmingFromApi(
    prewarming: ApiPrewarming | undefined,
    currencyInfo: CurrencyInfo
  ): ProductPricePrewarming | undefined {
    if (prewarming) {
      return {
        ...prewarming,
        priceWithCurrency: MoneyHelpers.formatPriceWithCurrencySymbol(
          Number.parseInt(prewarming.price, 10),
          currencyInfo
        )
      };
    }
    return undefined;
  }

  /**
   * Retrieves a specific configuration parameter from the growthbook config array.
   * @param growthbookConfig - Array of GrowthbookConfig objects that contain configuration settings
   * @param param - The key of the configuration parameter to retrieve
   * @returns The value of the specified configuration parameter as a string, boolean, Record<string, string> or
   * undefined if not found
   */
  public static getGrowthbookConfigurationsParams(
    growthbookConfig: GrowthbookConfig[],
    param: string
  ): string | boolean | Record<string, string> | undefined {
    return growthbookConfig.find((conf) => conf.key === param)?.value as string | boolean | Record<string, string>;
  }

}
